import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { confirmDialog } from '../../../components/ui/ConfirmDialog'
import FormDialog from '../../../components/ui/FormDialog'
import MobileSheetDropdown from '../../../components/ui/MobileSheetDropdown'
import { useToast } from '../../../components/ui/ToastProvider'
import ActionPeopleEditor from '../../actions/components/ActionPeopleEditor'
import { useStakeholders } from '../hooks/usePrincipleDecisions'
import type { MinutePerson, PrincipleDecision } from '../types'
import { toInput } from '../types'

interface Props {
  visible: boolean
  /** Sabit prensip kararı (satırdan açıldıysa); null → üstteki "Paydaş Ekle" (dropdown ile seçilir). */
  principle: PrincipleDecision | null
  /** Dropdown için tüm (sayfadaki) prensip kararları. */
  principles: PrincipleDecision[]
  /** Yazma yetkisi (Özel Kalem) → ekle/çıkar; yoksa salt görüntüleme. */
  canManage: boolean
  onHide: () => void
}

/**
 * Paydaş görüntüle / ekle / çıkar (analiz 5.5–5.7). Üstteki "Paydaş Ekle" akışında bir prensip kararı
 * numarası seçilir (dropdown); satırdan açıldığında kayıt sabittir. Mevcut paydaşlar çip olarak listelenir
 * (yetkiliyse X ile çıkarılır, onaylı); yeni paydaşlar HR aramasından çoklu eklenir (sicil bazında tekil).
 */
export default function StakeholdersDialog({
  visible,
  principle,
  principles,
  canManage,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const [selectedId, setSelectedId] = useState<number | null>(principle?.id ?? null)

  /**
   * Açılır panelin genişlik sınırı, seçim kutusunun genişliğinden ölçülür.
   *
   * <p><b>Neden JS ile ölçülüyor (saf CSS yetmiyor):</b> panel {@code document.body}'e eklendiği için
   * ona uygulanan {@code max-width: 100%} kutunun değil <b>gövdenin</b> genişliğine göre çözülür. Ayrıca
   * yalnız metin sarmasına izin vermek de yetmez: mutlak konumlu bir kutu "shrink-to-fit" ile boyutlanır
   * ve bu hesap {@code max-content}'i kullanır — yani en uzun satır kadar genişler, sarma devreye
   * girmez. Seçenek etiketi prensip AÇIKLAMASI olduğu için (2000 karaktere kadar) panel ekranı aşıyordu.
   *
   * <p>Ölçüm {@code useLayoutEffect} ile <b>boyama öncesi</b> yapılır (panel açıldığında değer hazırdır,
   * geniş panelin bir kare görünüp daralması yaşanmaz); {@code onShow} yalnız pencere yeniden
   * boyutlandıysa değeri tazeler.
   */
  const fieldRef = useRef<HTMLDivElement>(null)
  const [panelMaxWidth, setPanelMaxWidth] = useState<number>()
  const measurePanelWidth = () => {
    const width = fieldRef.current?.clientWidth
    if (width != null && width > 0) setPanelMaxWidth(width)
  }
  useLayoutEffect(measurePanelWidth, [])
  const [toAdd, setToAdd] = useState<MinutePerson[]>([])

  useEffect(() => {
    if (visible) {
      setSelectedId(principle?.id ?? null)
      setToAdd([])
    }
  }, [visible, principle])

  const { list, add, remove } = useStakeholders(selectedId)
  const current = useMemo(() => list.data ?? [], [list.data])

  const selected =
    principle ?? principles.find((p) => p.id === selectedId) ?? null

  const options = useMemo(
    () =>
      principles.map((p) => ({
        label: `${p.recordNumber ? `${p.recordNumber} — ` : ''}${p.description}`,
        value: p.id,
      })),
    [principles],
  )

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const onRemove = (person: MinutePerson) => {
    if (!selectedId) return
    confirmDialog({
      header: t('principleDecisions.stakeholders.removeTitle'),
      message: t('principleDecisions.stakeholders.removeConfirm', {
        name: person.fullName,
        number: selected?.recordNumber ?? '',
      }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('principleDecisions.stakeholders.removeAccept'),
      accept: () =>
        remove.mutate(
          { id: selectedId, employeeId: person.employeeId },
          { onSuccess: () => notify('success', t('principleDecisions.stakeholders.removed')) },
        ),
    })
  }

  const handleAdd = () => {
    if (!selectedId) {
      notify('error', t('principleDecisions.stakeholders.selectPrinciple'))
      return
    }
    if (toAdd.length === 0) {
      notify('error', t('principleDecisions.stakeholders.atLeastOne'))
      return
    }
    add.mutate(
      { id: selectedId, people: toAdd.map(toInput) },
      {
        onSuccess: () => {
          notify('success', t('principleDecisions.stakeholders.added'))
          setToAdd([])
        },
      },
    )
  }

  return (
    <FormDialog
      visible={visible}
      header={t('principleDecisions.stakeholders.title')}
      submitLabel={t('principleDecisions.stakeholders.add')}
      submitting={add.isPending}
      submitDisabled={!selectedId}
      // Yetkisiz kullanıcı (ör. Yönetici rolü) paydaş ekleyemez → Ekle/Kapat HİÇ çizilmez, pop-up
      // yalnız başlıktaki × ile kapanır (kullanıcı isteği 2026-08-28: pasif buton da istenmedi).
      hideFooter={!canManage}
      onSubmit={handleAdd}
      onHide={onHide}
      width="40rem"
    >
      <div className="admin-form">
        {/* Üst akış: hangi prensip kararına paydaş ekleneceği seçilir. Satırdan açıldıysa sabit başlık. */}
        {principle ? (
          <p className="ui-dialog-subject">
            {principle.recordNumber ? `${principle.recordNumber} — ` : ''}
            {principle.description}
          </p>
        ) : (
          <div className="pd-person-field" ref={fieldRef}>
            <span className="pd-person-label">
              {t('principleDecisions.stakeholders.principle')}
            </span>
            <MobileSheetDropdown
              value={selectedId}
              options={options}
              onChange={(e) => setSelectedId(e.value)}
              placeholder={t('principleDecisions.stakeholders.selectPlaceholder')}
              filter
              className="pd-sk-dropdown"
              // Genişlik sınırı `pd-sk-dropdown-panel` + ölçülen `panelStyle` ile HER genişlikte kutunun
              // kendi genişliğine çekilir (gerekçe: yukarıdaki `measurePanelWidth` açıklaması); ≤768px'te
              // `MobileSheetDropdown`'ın sheet'i bunu `width/max-width: 100% !important` ile yener, çakışma
              // yoktur (seçenek etiketi prensip AÇIKLAMASI'dır, 2000 karaktere kadar — taşma riski).
              panelClassName="pd-sk-dropdown-panel"
              panelStyle={panelMaxWidth != null ? { maxWidth: panelMaxWidth } : undefined}
              onShow={measurePanelWidth}
            />
          </div>
        )}

        {selectedId != null && (
          <>
            <div className="pd-person-field">
              <span className="pd-person-label">
                {t('principleDecisions.stakeholders.current')}
              </span>
              {current.length === 0 ? (
                <p className="act-empty-hint">{t('principleDecisions.stakeholders.empty')}</p>
              ) : (
                <ul className="act-follower-list">
                  {current.map((p) => (
                    <li key={p.employeeId} className="act-follower-chip">
                      <span className="act-follower-name">{p.fullName}</span>
                      <span className="act-follower-title">
                        {p.jobTitleTr ?? p.jobTitleEn ?? ''}
                      </span>
                      {canManage && (
                        <button
                          type="button"
                          className="act-follower-remove"
                          aria-label={t('principleDecisions.stakeholders.removeAccept')}
                          title={t('principleDecisions.stakeholders.removeAccept')}
                          onClick={() => onRemove(p)}
                        >
                          <i className="pi pi-times" aria-hidden="true" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {canManage && (
              <div className="pd-person-field">
                <span className="pd-person-label">{t('principleDecisions.stakeholders.addLabel')}</span>
                <ActionPeopleEditor
                  people={toAdd}
                  editable
                  onAdd={(p) => {
                    if (current.some((c) => c.employeeId === p.employeeId)) return
                    setToAdd((prev) =>
                      prev.some((x) => x.employeeId === p.employeeId)
                        ? prev
                        : [
                            ...prev,
                            {
                              employeeId: p.employeeId,
                              fullName: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.employeeId,
                              firstName: p.firstName ?? null,
                              lastName: p.lastName ?? null,
                              jobTitleTr: p.jobTitleTr ?? null,
                              jobTitleEn: p.jobTitleEn ?? null,
                              businessMail: p.businessMail ?? null,
                            },
                          ],
                    )
                  }}
                  onRemove={(employeeId) =>
                    setToAdd((prev) => prev.filter((x) => x.employeeId !== employeeId))
                  }
                  placeholder={t('principleDecisions.form.personPlaceholder')}
                  removeLabel={t('principleDecisions.form.removePerson')}
                />
              </div>
            )}
          </>
        )}
      </div>
    </FormDialog>
  )
}
